/**
 * Types for per-env design-token JSON under `src/themes/source/tokens/{EnvId}.json`
 * ([DTCG](https://www.designtokens.org/) leaves with `$type` / `$value`).
 */

import type { ClientConstants } from '@/constants/ClientConstants';
import { type Theme as AgGridTheme } from 'ag-grid-community';
import type { Preset } from '@primeuix/themes/types';
/**
 * Reserved base-theme id for stock PrimeVue / Aura reference themes.
 * Not a deploy client — keep out of ClientConstants / VITE_CLIENT.
 */
export const DESIGN_TOKEN_BASE_ID = 'PrimeVue' as const;

export type DesignTokenEnvId = ClientConstants | typeof DESIGN_TOKEN_BASE_ID;

export type DesignTokenColorScheme = 'light' | 'dark';

/**
 * One CSS box-shadow layer as exported from Figma (`x` / `y` / `blur` / `spread` / `color`).
 * Effect tokens use a single layer or an array of layers (stacked shadows).
 *
 * Single layer — @PrimeVue.json (13534-13543):
 * `{ "$value": { "x": "0", "y": "1", "blur": "2", "spread": "0", "color": "#1212170d" } }`
 *
 * Stacked layers — @PrimeVue.json (13557-13576):
 * `{ "$value": [{ "x": "0", "y": "2", "blur": "4", "spread": "-2", "color": "#0000001a" }, { "x": "0", "y": "4", "blur": "6", "spread": "-1", "color": "#0000001a" }] }`
 */
export interface DesignTokenShadowValue {
  x: string;
  y: string;
  blur: string;
  spread: string;
  color: string;
}

export type DesignTokenType = 'color' | 'number' | 'string';

export type DesignTokenValue = string | number | DesignTokenShadowValue | DesignTokenShadowValue[];

/**
 * DTCG leaf token (`$type` + `$value`).
 * Shadow leaves under `aura/effects` often omit `$type` and use a shadow object/array as `$value`.
 */
export interface DesignTokenLeaf {
  $type?: DesignTokenType;
  $value: DesignTokenValue;
}

/** Nested group or leaf under an `aura/*` collection. */
export type DesignTokenTree = {
  [segment: string]: DesignTokenLeaf | DesignTokenTree;
};

/**
 * Collection key prefix for split token documents (mirrored in `scripts/themes/figmaSource.ts`).
 * Scripts cannot value-import this module (Vite `@/` aliases), so keep both in sync.
 */
export const TOKEN_COLLECTION_PREFIX = 'aura/' as const;
export const EFFECTS_COLLECTION = `${TOKEN_COLLECTION_PREFIX}effects` as const;

type AuraCollectionKey =
  | `${typeof TOKEN_COLLECTION_PREFIX}${
      | 'component/light'
      | 'primitive'
      | 'semantic/light'
      | 'app'
      | 'custom'
      | 'semantic/common'
      | 'component/common'}`
  | typeof EFFECTS_COLLECTION;

/**
 * One theme’s split token document (normalized collection keys from `yarn theme:splitTokens`).
 * Light and dark are separate theme files (`{EnvId}.json` / `{EnvId}Dark.json`);
 * scheme collections always live under the `/light` keys.
 *
 * `EFFECTS_COLLECTION` holds shared component shadow tokens (focus rings, overlays, elevation).
 */
export type DesignTokenTheme = {
  source: string | null;
} & Record<AuraCollectionKey, DesignTokenTree>;

/**
 * App theme object containing the theme tokens, primevue preset, and ag-grid theme.
 */
export type AppTheme = {
  themeTokens: DesignTokenTheme;
  primevuePreset: Preset;
  agGridTheme: AgGridTheme;
};
